// Risk Score Calculator for Silk Road Terminal
// Calculates investment risk score based on multiple factors

import type { CountryOverview } from '../../types/country';

export interface RiskScoreResult {
  overall: number;           // 0-100 (higher = more risk)
  breakdown: {
    macro: number;           // Macroeconomic risk
    fiscal: number;          // Fiscal risk
    external: number;        // External sector risk
    political: number;       // Political/governance risk
    currency: number;        // Currency risk
  };
  level: 'Low' | 'Moderate' | 'Elevated' | 'High' | 'Severe';
  color: string;
  warnings: RiskWarning[];
}

export interface RiskWarning {
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  message: string;
  indicator: string;
  threshold: string;
}

// Risk thresholds
const THRESHOLDS = {
  inflation: { high: 15, critical: 25 },
  publicDebt: { high: 70, critical: 100 },
  externalDebt: { high: 80, critical: 120 },
  currentAccount: { high: -7, critical: -12 },
  reserves: { low: 3, critical: 2 },
  gdpGrowth: { low: 1, negative: 0 },
};

// Normalize to 0-100 risk scale
const normalizeRisk = (
  value: number | null,
  goodValue: number,
  badValue: number
): number => {
  if (value === null) return 50;

  if (goodValue < badValue) {
    // Higher is worse (e.g., debt, inflation)
    if (value <= goodValue) return 0;
    if (value >= badValue) return 100;
    return ((value - goodValue) / (badValue - goodValue)) * 100;
  } else {
    // Lower is worse (e.g., reserves, growth)
    if (value >= goodValue) return 0;
    if (value <= badValue) return 100;
    return ((goodValue - value) / (goodValue - badValue)) * 100;
  }
};

// Calculate macroeconomic risk
const calculateMacroRisk = (data: CountryOverview): { risk: number; warnings: RiskWarning[] } => {
  const warnings: RiskWarning[] = [];
  let risk = 0;

  // Inflation risk (30% weight)
  if (data.inflation.cpi !== null) {
    const inflationRisk = normalizeRisk(data.inflation.cpi, 5, 30);
    risk += inflationRisk * 0.3;

    if (data.inflation.cpi >= THRESHOLDS.inflation.critical) {
      warnings.push({
        severity: 'critical',
        category: 'Inflation',
        message: `Hyperinflation risk: CPI at ${data.inflation.cpi.toFixed(1)}%`,
        indicator: 'inflation_cpi',
        threshold: `>${THRESHOLDS.inflation.critical}%`,
      });
    } else if (data.inflation.cpi >= THRESHOLDS.inflation.high) {
      warnings.push({
        severity: 'high',
        category: 'Inflation',
        message: `High inflation: CPI at ${data.inflation.cpi.toFixed(1)}%`,
        indicator: 'inflation_cpi',
        threshold: `>${THRESHOLDS.inflation.high}%`,
      });
    }
  }

  // GDP growth risk (30% weight)
  if (data.gdp.growth !== null) {
    const growthRisk = normalizeRisk(data.gdp.growth, 5, -3);
    risk += growthRisk * 0.3;

    if (data.gdp.growth < THRESHOLDS.gdpGrowth.negative) {
      warnings.push({
        severity: 'high',
        category: 'Growth',
        message: `Economic contraction: GDP at ${data.gdp.growth.toFixed(1)}%`,
        indicator: 'gdp_growth',
        threshold: `<${THRESHOLDS.gdpGrowth.negative}%`,
      });
    } else if (data.gdp.growth < THRESHOLDS.gdpGrowth.low) {
      warnings.push({
        severity: 'medium',
        category: 'Growth',
        message: `Slow growth: GDP at ${data.gdp.growth.toFixed(1)}%`,
        indicator: 'gdp_growth',
        threshold: `<${THRESHOLDS.gdpGrowth.low}%`,
      });
    }
  }

  // Unemployment risk (20% weight) - using labor data if available
  // GDP per capita as economic development proxy (20% weight)
  if (data.gdp.perCapita !== null) {
    const developmentRisk = normalizeRisk(data.gdp.perCapita, 10000, 1000);
    risk += developmentRisk * 0.4;
  }

  return { risk: Math.min(100, Math.max(0, risk)), warnings };
};

// Calculate fiscal risk
const calculateFiscalRisk = (data: CountryOverview): { risk: number; warnings: RiskWarning[] } => {
  const warnings: RiskWarning[] = [];
  let risk = 0;

  // Public debt risk (60% weight)
  if (data.debt.publicDebt !== null) {
    const debtRisk = normalizeRisk(data.debt.publicDebt, 40, 120);
    risk += debtRisk * 0.6;

    if (data.debt.publicDebt >= THRESHOLDS.publicDebt.critical) {
      warnings.push({
        severity: 'critical',
        category: 'Fiscal',
        message: `Unsustainable debt: ${data.debt.publicDebt.toFixed(1)}% of GDP`,
        indicator: 'govt_debt_gdp',
        threshold: `>${THRESHOLDS.publicDebt.critical}%`,
      });
    } else if (data.debt.publicDebt >= THRESHOLDS.publicDebt.high) {
      warnings.push({
        severity: 'high',
        category: 'Fiscal',
        message: `Elevated debt: ${data.debt.publicDebt.toFixed(1)}% of GDP`,
        indicator: 'govt_debt_gdp',
        threshold: `>${THRESHOLDS.publicDebt.high}%`,
      });
    }
  }

  // Debt servicing risk (40% weight)
  if (data.debt.debtService !== null) {
    const serviceRisk = normalizeRisk(data.debt.debtService, 10, 30);
    risk += serviceRisk * 0.4;

    if (data.debt.debtService > 20) {
      warnings.push({
        severity: 'high',
        category: 'Fiscal',
        message: `High debt service: ${data.debt.debtService.toFixed(1)}% of exports`,
        indicator: 'debt_service',
        threshold: '>20%',
      });
    }
  }

  return { risk: Math.min(100, Math.max(0, risk)), warnings };
};

// Calculate external sector risk
const calculateExternalRisk = (data: CountryOverview): { risk: number; warnings: RiskWarning[] } => {
  const warnings: RiskWarning[] = [];
  let risk = 0;

  // Current account risk (30% weight)
  if (data.trade.currentAccount !== null) {
    const caRisk = normalizeRisk(data.trade.currentAccount, 0, -15);
    risk += caRisk * 0.3;

    if (data.trade.currentAccount <= THRESHOLDS.currentAccount.critical) {
      warnings.push({
        severity: 'critical',
        category: 'External',
        message: `Unsustainable deficit: ${data.trade.currentAccount.toFixed(1)}% of GDP`,
        indicator: 'current_account_gdp',
        threshold: `<${THRESHOLDS.currentAccount.critical}%`,
      });
    } else if (data.trade.currentAccount <= THRESHOLDS.currentAccount.high) {
      warnings.push({
        severity: 'high',
        category: 'External',
        message: `Large deficit: ${data.trade.currentAccount.toFixed(1)}% of GDP`,
        indicator: 'current_account_gdp',
        threshold: `<${THRESHOLDS.currentAccount.high}%`,
      });
    }
  }

  // Reserves adequacy risk (35% weight)
  if (data.reserves.monthsImport !== null) {
    const reservesRisk = normalizeRisk(data.reserves.monthsImport, 6, 1);
    risk += reservesRisk * 0.35;

    if (data.reserves.monthsImport <= THRESHOLDS.reserves.critical) {
      warnings.push({
        severity: 'critical',
        category: 'External',
        message: `Critical reserves: ${data.reserves.monthsImport.toFixed(1)} months import cover`,
        indicator: 'reserves_months',
        threshold: `<${THRESHOLDS.reserves.critical} months`,
      });
    } else if (data.reserves.monthsImport <= THRESHOLDS.reserves.low) {
      warnings.push({
        severity: 'medium',
        category: 'External',
        message: `Low reserves: ${data.reserves.monthsImport.toFixed(1)} months import cover`,
        indicator: 'reserves_months',
        threshold: `<${THRESHOLDS.reserves.low} months`,
      });
    }
  }

  // External debt risk (35% weight)
  if (data.debt.externalDebt !== null && data.gdp.value !== null) {
    const externalDebtGdp = (data.debt.externalDebt / data.gdp.value) * 100;
    const extDebtRisk = normalizeRisk(externalDebtGdp, 40, 150);
    risk += extDebtRisk * 0.35;

    if (externalDebtGdp >= THRESHOLDS.externalDebt.critical) {
      warnings.push({
        severity: 'critical',
        category: 'External',
        message: `Excessive external debt: ${externalDebtGdp.toFixed(1)}% of GDP`,
        indicator: 'external_debt_gdp',
        threshold: `>${THRESHOLDS.externalDebt.critical}%`,
      });
    } else if (externalDebtGdp >= THRESHOLDS.externalDebt.high) {
      warnings.push({
        severity: 'high',
        category: 'External',
        message: `High external debt: ${externalDebtGdp.toFixed(1)}% of GDP`,
        indicator: 'external_debt_gdp',
        threshold: `>${THRESHOLDS.externalDebt.high}%`,
      });
    }
  }

  return { risk: Math.min(100, Math.max(0, risk)), warnings };
};

// Calculate political/governance risk (based on available proxies)
const calculatePoliticalRisk = (data: CountryOverview): { risk: number; warnings: RiskWarning[] } => {
  const warnings: RiskWarning[] = [];
  let risk = 50; // Default to moderate if no data

  // Use credit rating as proxy for institutional quality
  if (data.ratings.sp) {
    const ratingRisk = 100 - getRatingScore(data.ratings.sp);
    risk = ratingRisk;

    if (ratingRisk > 60) {
      warnings.push({
        severity: 'medium',
        category: 'Governance',
        message: `Sub-investment grade: ${data.ratings.sp} rating`,
        indicator: 'credit_rating',
        threshold: 'Below BBB-',
      });
    }
  }

  return { risk: Math.min(100, Math.max(0, risk)), warnings };
};

// Calculate currency risk
const calculateCurrencyRisk = (data: CountryOverview): { risk: number; warnings: RiskWarning[] } => {
  const warnings: RiskWarning[] = [];
  let risk = 50;

  // High inflation implies currency risk
  if (data.inflation.cpi !== null) {
    const inflationCurrencyRisk = normalizeRisk(data.inflation.cpi, 3, 20);
    risk = inflationCurrencyRisk * 0.5;
  }

  // Low reserves implies currency risk
  if (data.reserves.monthsImport !== null) {
    const reservesCurrencyRisk = normalizeRisk(data.reserves.monthsImport, 6, 2);
    risk += reservesCurrencyRisk * 0.3;
  }

  // Large current account deficit implies currency risk
  if (data.trade.currentAccount !== null) {
    const caCurrencyRisk = normalizeRisk(data.trade.currentAccount, 0, -10);
    risk += caCurrencyRisk * 0.2;
  }

  if (risk > 70) {
    warnings.push({
      severity: 'high',
      category: 'Currency',
      message: 'Elevated currency depreciation risk',
      indicator: 'currency_risk',
      threshold: 'Multiple factors',
    });
  }

  return { risk: Math.min(100, Math.max(0, risk)), warnings };
};

// Convert credit rating to score
const getRatingScore = (rating: string): number => {
  const ratings: Record<string, number> = {
    'AAA': 100, 'AA+': 95, 'AA': 90, 'AA-': 85,
    'A+': 80, 'A': 75, 'A-': 70,
    'BBB+': 65, 'BBB': 60, 'BBB-': 55,
    'BB+': 50, 'BB': 45, 'BB-': 40,
    'B+': 35, 'B': 30, 'B-': 25, 'B1': 30,
    'CCC+': 20, 'CCC': 15, 'CCC-': 10,
    'CC': 5, 'C': 3, 'D': 0,
  };
  return ratings[rating] || 40;
};

// Get risk level label
const getRiskLevel = (score: number): 'Low' | 'Moderate' | 'Elevated' | 'High' | 'Severe' => {
  if (score < 20) return 'Low';
  if (score < 40) return 'Moderate';
  if (score < 60) return 'Elevated';
  if (score < 80) return 'High';
  return 'Severe';
};

// Get risk color
const getRiskColor = (score: number): string => {
  if (score < 20) return '#22c55e'; // Green
  if (score < 40) return '#84cc16'; // Lime
  if (score < 60) return '#eab308'; // Yellow
  if (score < 80) return '#f97316'; // Orange
  return '#ef4444'; // Red
};

/**
 * Calculate overall risk score for a country
 */
export const calculateRiskScore = (data: CountryOverview): RiskScoreResult => {
  const macroResult = calculateMacroRisk(data);
  const fiscalResult = calculateFiscalRisk(data);
  const externalResult = calculateExternalRisk(data);
  const politicalResult = calculatePoliticalRisk(data);
  const currencyResult = calculateCurrencyRisk(data);

  // Calculate weighted overall score
  const overall =
    macroResult.risk * 0.25 +
    fiscalResult.risk * 0.20 +
    externalResult.risk * 0.25 +
    politicalResult.risk * 0.15 +
    currencyResult.risk * 0.15;

  // Combine and sort all warnings
  const warnings = [
    ...macroResult.warnings,
    ...fiscalResult.warnings,
    ...externalResult.warnings,
    ...politicalResult.warnings,
    ...currencyResult.warnings,
  ].sort((a, b) => {
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });

  const roundedOverall = Math.round(overall);

  return {
    overall: roundedOverall,
    breakdown: {
      macro: Math.round(macroResult.risk),
      fiscal: Math.round(fiscalResult.risk),
      external: Math.round(externalResult.risk),
      political: Math.round(politicalResult.risk),
      currency: Math.round(currencyResult.risk),
    },
    level: getRiskLevel(roundedOverall),
    color: getRiskColor(roundedOverall),
    warnings: warnings.slice(0, 10), // Top 10 warnings
  };
};

export default calculateRiskScore;
